<section class="page-stack">
  <div class="section-header">
    <div>
      <p class="eyebrow mb-1">Employer Setup</p>
      <h2>{{ employer?.company_name || 'Employer' }}</h2>
    </div>
    <div class="table-actions">
      @if (employer) {
        <span class="status-pill" [class.status-muted]="employer.status !== 'active'">{{ employer.status }}</span>
      }
    </div>
  </div>

  @if (notice) {
    <div class="alert alert-info">{{ notice }}</div>
  }
  @if (error) {
    <div class="alert alert-danger">{{ error }}</div>
  }

  @if (loading) {
    <div class="panel p-4 text-muted-soft">Loading employer...</div>
  }

  @if (employer) {
    <div class="panel p-3">
      <div class="section-header mb-3">
        <div>
          <p class="eyebrow mb-1">Company Details</p>
          <h2>Employer profile</h2>
        </div>
        <div class="table-actions">
          @if (!editingEmployer) {
            <button class="btn btn-sm btn-outline-info" type="button" (click)="editingEmployer = true">
              <i class="bi bi-pencil-square"></i>
              Edit
            </button>
          }
        </div>
      </div>

      @if (!editingEmployer) {
        <div class="detail-grid">
          <div class="detail-item">
            <span>Company name</span>
            <strong>{{ employer.company_name || '-' }}</strong>
          </div>
          <div class="detail-item">
            <span>Physical location</span>
            <strong>{{ employer.business_location || '-' }}</strong>
          </div>
          <div class="detail-item">
            <span>Contact email</span>
            <strong>{{ employer.contact_email || '-' }}</strong>
          </div>
          <div class="detail-item">
            <span>Contact phone</span>
            <strong>{{ employer.contact_phone || '-' }}</strong>
          </div>
        </div>
      } @else {
        <form class="row g-3" (ngSubmit)="save()">
          <div class="col-md-6">
            <label class="form-label">Company name</label>
            <input class="form-control" name="company_name" [(ngModel)]="employer.company_name">
          </div>
          <div class="col-md-6">
            <label class="form-label">Physical location</label>
            <input class="form-control" name="business_location" [(ngModel)]="employer.business_location">
          </div>
          <div class="col-md-6">
            <label class="form-label">Contact email</label>
            <input class="form-control" name="contact_email" [(ngModel)]="employer.contact_email">
          </div>
          <div class="col-md-6">
            <label class="form-label">Contact phone</label>
            <input class="form-control" name="contact_phone" [(ngModel)]="employer.contact_phone">
          </div>
          <div class="col-12 table-actions justify-content-start">
            <button class="btn btn-accent" [disabled]="saving">{{ saving ? 'Saving...' : 'Save details' }}</button>
            <button class="btn btn-outline-light" type="button" (click)="cancelEmployerEdit()" [disabled]="saving">Cancel</button>
          </div>
        </form>
      }
    </div>

    <div class="panel p-3">
      <div class="section-header mb-3">
        <div>
          <p class="eyebrow mb-1">Portal Access</p>
          <h2>Employer users</h2>
        </div>
        <div class="table-actions">
          <button class="btn btn-sm btn-accent" type="button" (click)="showingCreateEmployerUser = !showingCreateEmployerUser">
            <i class="bi" [class.bi-plus-lg]="!showingCreateEmployerUser" [class.bi-x-lg]="showingCreateEmployerUser"></i>
            {{ showingCreateEmployerUser ? 'Close' : 'New user' }}
          </button>
        </div>
      </div>

      @if (showingCreateEmployerUser) {
        <form class="row g-3 align-items-end mb-3" (ngSubmit)="createEmployerUser()">
          <div class="col-md-4">
            <label class="form-label">Full name</label>
            <input class="form-control" name="employer_user_full_name" [(ngModel)]="employerUserForm.full_name">
          </div>
          <div class="col-md-4">
            <label class="form-label">Email</label>
            <input class="form-control" type="email" name="employer_user_email" [(ngModel)]="employerUserForm.email">
          </div>
          <div class="col-md-2">
            <label class="form-label">Role</label>
            <select class="form-select" name="employer_user_role" [(ngModel)]="employerUserForm.role">
              <option value="employer_admin">Employer admin</option>
              <option value="payroll_admin">Payroll admin</option>
            </select>
          </div>
          <div class="col-md-2">
            <button class="btn btn-accent w-100" [disabled]="creatingEmployerUser || !employerUserForm.full_name || !employerUserForm.email">
              {{ creatingEmployerUser ? 'Creating...' : 'Create user' }}
            </button>
          </div>
        </form>
      }

      <div class="table-responsive">
        <table class="table table-dark table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            @for (user of employerUsers; track user.id) {
              <tr>
                <td class="fw-semibold">{{ user.full_name }}</td>
                <td>{{ user.email }}</td>
                <td>{{ user.role }}</td>
                <td>
                  <span class="status-pill" [class.status-muted]="user.status !== 'active'">{{ user.status }}</span>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="4" class="text-center py-4 text-muted-soft">
                  {{ loadingEmployerUsers ? 'Loading employer users...' : 'No employer users yet.' }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    <div class="panel p-3">
      <div class="section-header mb-3">
        <div>
          <p class="eyebrow mb-1">Documents</p>
          <h2>Activation requirements</h2>
        </div>
      </div>

      <div class="document-grid">
        <div class="document-card">
          <div class="document-card-main">
            <strong>Registration certificate</strong>
            <span>{{ employer.registration_certificate_url ? 'Uploaded' : 'Required' }}</span>
          </div>
          <div class="document-actions">
            @if (employer.registration_certificate_url) {
              <button class="btn btn-sm btn-ghost-cyan" type="button" (click)="viewDocument(employer.registration_certificate_url, 'registration_certificate_url')">
                <i class="bi bi-eye"></i>
                {{ viewing['registration_certificate_url'] ? 'Opening...' : 'View' }}
              </button>
            }
            <label class="btn btn-sm btn-outline-info mb-0">
              <i class="bi bi-cloud-upload"></i>
              {{ uploading['registration_certificate_url'] ? 'Uploading...' : 'Upload' }}
              <input type="file" hidden (change)="uploadDocument($event, 'registration_certificate_url', 'employer-registration-certificate')">
            </label>
          </div>
        </div>
        <div class="document-card">
          <div class="document-card-main">
            <strong>KRA PIN</strong>
            <span>{{ employer.kra_pin_url ? 'Uploaded' : 'Required' }}</span>
          </div>
          <div class="document-actions">
            @if (employer.kra_pin_url) {
              <button class="btn btn-sm btn-ghost-cyan" type="button" (click)="viewDocument(employer.kra_pin_url, 'kra_pin_url')">
                <i class="bi bi-eye"></i>
                {{ viewing['kra_pin_url'] ? 'Opening...' : 'View' }}
              </button>
            }
            <label class="btn btn-sm btn-outline-info mb-0">
              <i class="bi bi-cloud-upload"></i>
              {{ uploading['kra_pin_url'] ? 'Uploading...' : 'Upload' }}
              <input type="file" hidden (change)="uploadDocument($event, 'kra_pin_url', 'employer-kra-pin')">
            </label>
          </div>
        </div>
        <div class="document-card">
          <div class="document-card-main">
            <strong>Tax compliance</strong>
            <span>{{ employer.tax_compliance_url ? 'Expires ' + employer.tax_compliance_expires_at : 'Expires after 1 year' }}</span>
          </div>
          <div class="document-actions">
            @if (employer.tax_compliance_url) {
              <button class="btn btn-sm btn-ghost-cyan" type="button" (click)="viewDocument(employer.tax_compliance_url, 'tax_compliance_url')">
                <i class="bi bi-eye"></i>
                {{ viewing['tax_compliance_url'] ? 'Opening...' : 'View' }}
              </button>
            }
            <label class="btn btn-sm btn-outline-info mb-0">
              <i class="bi bi-cloud-upload"></i>
              {{ uploading['tax_compliance_url'] ? 'Uploading...' : 'Upload' }}
              <input type="file" hidden (change)="uploadDocument($event, 'tax_compliance_url', 'employer-tax-compliance')">
            </label>
          </div>
        </div>
        <div class="document-card">
          <div class="document-card-main">
            <strong>Business CR12</strong>
            <span>{{ employer.cr12_url ? 'Expires ' + employer.cr12_expires_at : 'Expires after 6 months' }}</span>
          </div>
          <div class="document-actions">
            @if (employer.cr12_url) {
              <button class="btn btn-sm btn-ghost-cyan" type="button" (click)="viewDocument(employer.cr12_url, 'cr12_url')">
                <i class="bi bi-eye"></i>
                {{ viewing['cr12_url'] ? 'Opening...' : 'View' }}
              </button>
            }
            <label class="btn btn-sm btn-outline-info mb-0">
              <i class="bi bi-cloud-upload"></i>
              {{ uploading['cr12_url'] ? 'Uploading...' : 'Upload' }}
              <input type="file" hidden (change)="uploadDocument($event, 'cr12_url', 'employer-cr12')">
            </label>
          </div>
        </div>
        <div class="document-card">
          <div class="document-card-main">
            <strong>Signed MOU</strong>
            <span>{{ employer.signed_mou_url ? 'Uploaded' : 'Required before activation' }}</span>
          </div>
          <div class="document-actions">
            @if (employer.signed_mou_url) {
              <button class="btn btn-sm btn-ghost-cyan" type="button" (click)="viewDocument(employer.signed_mou_url, 'signed_mou_url')">
                <i class="bi bi-eye"></i>
                {{ viewing['signed_mou_url'] ? 'Opening...' : 'View' }}
              </button>
            }
            <label class="btn btn-sm btn-outline-info mb-0">
              <i class="bi bi-cloud-upload"></i>
              {{ uploading['signed_mou_url'] ? 'Uploading...' : 'Upload' }}
              <input type="file" hidden (change)="uploadDocument($event, 'signed_mou_url', 'employer-signed-mou')">
            </label>
          </div>
        </div>
      </div>
    </div>
  }

</section>
